import React, { FC, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Spin } from 'antd';
import {
	CheckOutlined,
	ClockCircleOutlined,
	HourglassOutlined,
	InfoCircleOutlined,
	MinusSquareOutlined,
	PlusSquareOutlined,
	SelectOutlined,
} from '@ant-design/icons';
import { fetchComplianceRequestInstancesList } from '../../thunks/compliance_request_instances_thunks';
import { withUserType } from '../withUserType';
import { COMPLIANCE_INSTANCE_STATUSES, ComplianceRequest } from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import InlineHyperlink from '../common/InlineHyperlink';

// Purple/7 from the design — the "+N more" reveal.
const MORE_COLOR = '#54298E';
// Supplier chips shown before "+N more".
const MAX_CHIPS = 9;

const STATUS_LINES = [
	{
		key: COMPLIANCE_INSTANCE_STATUSES.WAITING_ON_SUPPLIER,
		label: 'Waiting on Supplier',
		icon: <HourglassOutlined style={{ color: 'rgb(219, 123, 43)' }} />,
	},
	{
		key: COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW,
		label: 'Pending Approval',
		icon: <ClockCircleOutlined style={{ color: 'rgb(171, 133, 15)' }} />,
	},
	{
		key: COMPLIANCE_INSTANCE_STATUSES.APPROVED,
		label: 'Approved',
		icon: <CheckOutlined style={{ color: 'rgb(27, 121, 34)' }} />,
	},
	{
		key: COMPLIANCE_INSTANCE_STATUSES.REJECTED,
		label: 'Rejected',
		icon: <InfoCircleOutlined style={{ color: 'rgb(204, 50, 50)' }} />,
	},
];

interface StatusLineProps {
	requestId: number;
	statusKey: string;
	label: string;
	icon: React.ReactNode;
	count: number;
	fetchInstances: (params: any) => Promise<any[]>;
}

// One status line: "[+] <icon> <label>: <count>". The [+] expands the suppliers
// in that status inline (lazy-fetched) as grey chips, capped with "+N more".
const StatusLine: FC<StatusLineProps> = ({
	requestId,
	statusKey,
	label,
	icon,
	count,
	fetchInstances,
}): React.ReactElement => {
	const [expanded, setExpanded] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [loading, setLoading] = useState(false);
	// null = not yet fetched
	const [suppliers, setSuppliers] = useState(null);

	const toggle = () => {
		const next = !expanded;
		setExpanded(next);
		if (next && suppliers === null) {
			setLoading(true);
			fetchInstances({ complianceRequestId: requestId, statuses: statusKey }).then(
				(instances) => {
					setSuppliers(
						(instances || []).map((instance) => ({
							id: nullSafeGet('supplierFacilityId', instance),
							name: nullSafeGetOrElse('supplierFacilityName', instance, '--'),
						}))
					);
					setLoading(false);
				},
				() => {
					setSuppliers([]);
					setLoading(false);
				}
			);
		}
	};

	const allSuppliers = suppliers || [];
	const shownSuppliers = showAll ? allSuppliers : allSuppliers.slice(0, MAX_CHIPS);
	const remaining = allSuppliers.length - shownSuppliers.length;

	return (
		<div className="flex flex-col gap-1">
			<div className="bodyregular flex flex-row items-center gap-1 text-character-primary">
				<span
					role="button"
					onClick={toggle}
					className="flex cursor-pointer items-center text-grey-7"
				>
					{expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
				</span>
				{icon}
				<span>
					{label}: {count}
				</span>
			</div>
			{expanded ? (
				loading ? (
					<div className="pl-5 pt-1">
						<Spin size="small" />
					</div>
				) : (
					<div className="flex flex-row flex-wrap items-center gap-2 pl-5">
						{shownSuppliers.map((supplier, index) => (
							<div
								key={index}
								className="footnoteregular inline-flex items-center gap-1 rounded-sm border border-solid border-grey-5 bg-grey-2 px-2"
							>
								<InlineHyperlink
									text={supplier.name}
									path={`/buyer/suppliers/detail/${supplier.id}`}
								/>
								<SelectOutlined style={{ fontSize: 9, color: '#8c8c8c' }} />
							</div>
						))}
						{remaining > 0 ? (
							<span
								role="button"
								onClick={() => setShowAll(true)}
								className="bodyregular cursor-pointer"
								style={{ color: MORE_COLOR }}
							>
								<span className="bodybold">+{remaining}</span> more
							</span>
						) : null}
					</div>
				)
			) : null}
		</div>
	);
};

interface ApprovalStatusCountsProps {
	roleType?: string;
	complianceRequest: ComplianceRequest;
	fetchInstances: (params: any) => Promise<any[]>;
}

// Published-request approval status: one expandable line per non-zero instance
// status, with the compliance due date footnote (also shown in the Expiration
// Date column, per design).
const ApprovalStatusCounts: FC<ApprovalStatusCountsProps> = ({
	complianceRequest,
	fetchInstances,
}): React.ReactElement => {
	const counts = nullSafeGetOrElse('instanceStatusCounts', complianceRequest, {});
	const lines = STATUS_LINES.filter((line) => nullSafeGet(line.key, counts));
	const dueBy = nullSafeGet('dueBy', complianceRequest);
	return (
		<div className="flex flex-col gap-2">
			{lines.length > 0 ? (
				lines.map((line) => (
					<StatusLine
						key={line.key}
						requestId={complianceRequest.id}
						statusKey={line.key}
						label={line.label}
						icon={line.icon}
						count={counts[line.key]}
						fetchInstances={fetchInstances}
					/>
				))
			) : (
				<span className="text-grey-8">No supplier responses yet</span>
			)}
			{complianceRequest.isMandatory && dueBy ? (
				<div className="footnoteregular flex flex-row items-center gap-1 text-character-secondary">
					<span>Due: {moment(dueBy).format('D MMM, YYYY')}</span>
					<InfoCircleOutlined />
				</div>
			) : null}
		</div>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		fetchInstances: (params) => dispatch(fetchComplianceRequestInstancesList(role)(params)),
	};
};

export default withUserType(connect(null, mapDispatchToProps)(ApprovalStatusCounts));
